import {useContext, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import {HardFactsFieldsLanguageSkillsList} from './HardFactsFieldsLanguageSkillsList';
import {EmpionButton} from '../../../../../../../../../components/EmpionButton/EmpionButton';
import {LanguageContext} from '../../../../../../../../../contexts/LanguageContext/LanguageContext';
import {getFieldStyle} from '../../../../../../../../../layouts/theme/FieldStyles';
import {SUPPORTED_LANGUAGE_LEVELS} from '../../../../../../../../../lib/constants';
import {ALL_LANGUAGES_LIST} from '../../../../../../../../../lib/languagesListConst';
import {LanguageSkill} from '../../../../../types/JobPostingsTypes';

interface HardFactsFieldsLanguageSkillsDialogProps {
  formik: any;
  dialogOpen: boolean;
  onClose(): void;
  onUpdate(languageSkills: Partial<LanguageSkill>[]): void;
}

export const HardFactsFieldsLanguageSkillsDialog = ({
  formik,
  dialogOpen,
  onClose,
  onUpdate,
}: HardFactsFieldsLanguageSkillsDialogProps) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const currentSystemLanguage = useContext(LanguageContext);

  const [language, setLanguage] = useState('');
  const [level, setLevel] = useState('');
  const [languageSkillsList, setLanguageSkillsList] = useState(formik.values.languageSkills);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const localisedLanguageOptions = useMemo(() => {
    return ALL_LANGUAGES_LIST.map((language) => ({
      id: language.id,
      label: language.label[currentSystemLanguage],
    }));
  }, [currentSystemLanguage]);

  const localisedLanguageLevelOptions = useMemo(() => {
    return SUPPORTED_LANGUAGE_LEVELS.map((languageLevel) => ({
      id: languageLevel,
      label: languageLevel,
    }));
  }, []);

  const clearState = () => {
    setLanguage('');
    setLevel('');
    setErrorMessage('');
  };

  const closeLanguageSkillsDialog = () => {
    clearState();
    dialogOpen = false;
    onClose();
  };

  const addNewLanguage = () => {
    if (!language || !level) {
      setErrorMessage(t('companyJobPostings.newJobDialog.formValidations.languageAndLevelRequired').toString());
      return;
    }

    // If the language already exists in the list, update it
    const languageAlreadyExists = languageSkillsList.find(
      (languageSkill: Partial<LanguageSkill>) => languageSkill.language === language
    );

    if (languageAlreadyExists) {
      const updatedLanguageSkillsList = languageSkillsList.map((languageSkill: Partial<LanguageSkill>) => {
        if (languageSkill.language === language) {
          return {
            language: language,
            level: level,
          };
        } else {
          return languageSkill;
        }
      });

      setLanguageSkillsList(updatedLanguageSkillsList);
      onUpdate(updatedLanguageSkillsList);
      return;
    }

    setErrorMessage('');

    setLanguageSkillsList([
      ...languageSkillsList,
      {
        language: language,
        level: level,
      },
    ]);

    const updatedLanguageSkills = [
      ...formik.values.languageSkills,
      {
        language: language,
        level: level,
      },
    ];

    formik.setFieldValue('languageSkills', updatedLanguageSkills);

    onUpdate(updatedLanguageSkills);
    clearState();
  };

  const removeLanguageSkill = (index: number) => {
    const updatedLanguageSkills = [...formik.values.languageSkills];
    updatedLanguageSkills.splice(index, 1);
    formik.setFieldValue('languageSkills', updatedLanguageSkills);
    setLanguageSkillsList(updatedLanguageSkills);
    onUpdate(updatedLanguageSkills);
    clearState();
  };

  const editLanguageSkill = (languageSkill: LanguageSkill) => {
    setLanguage(languageSkill.language);
    setLevel(languageSkill.level);
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={closeLanguageSkillsDialog}
        sx={{
          '& .MuiDialog-paper': {
            backgroundImage: 'none',
            minWidth: {xs: '100%', sm: '50%', md: '30%'},
          },
        }}
      >
        <DialogTitle id='form-dialog-title'>{t('companyJobPostings.newJobDialog.formLabels.languages')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('companyJobPostings.newJobDialog.formPlaceholders.languages')}</DialogContentText>
          <Grid container sx={{gap: '16px', mt: 5}}>
            <Grid item xs={12} sm={12}>
              <TextField
                select
                fullWidth
                label={t('companyJobPostings.newJobDialog.formLabels.languages')}
                name='newLanguageId'
                sx={getFieldStyle(theme)}
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
              >
                {localisedLanguageOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={12}>
              <TextField
                select
                fullWidth
                label={t('companyJobPostings.newJobDialog.formLabels.level')}
                name='newLanguageLevel'
                sx={getFieldStyle(theme)}
                value={level}
                onChange={(event) => setLevel(event.target.value)}
              >
                {localisedLanguageLevelOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {errorMessage && (
              <Typography variant='body1' color='error'>
                {errorMessage}
              </Typography>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <EmpionButton onClick={closeLanguageSkillsDialog} color='primary'>
            {t('shared.formButtons.close')}
          </EmpionButton>
          <EmpionButton onClick={addNewLanguage}>{t('shared.formButtons.add')}</EmpionButton>
        </DialogActions>
        <HardFactsFieldsLanguageSkillsList
          languageSkills={languageSkillsList}
          onRemove={removeLanguageSkill}
          onEdit={editLanguageSkill}
        />
      </Dialog>
    </>
  );
};
